import { useEditor, useNode } from "@craftjs/core";
import { getRandomId as getRandomNodeId } from "@craftjs/utils";
import React, { useState, useContext, useMemo, useEffect } from "react";
import { Resizable, RichTextInput } from "../../input";
import { ImageSettings } from "./settings";
import { CMSImageSettings } from "./cms-settings";
import GrigoraCSSRenderer from "./../css-render";
import LoaderSmall from "../../loader/loadersmall";
import { GrigoraDashboardContext } from "../../dashboard/context";
import ImagePickerUIKit from "../../ui-kit/image";
import icon from "./icon";
import { getBlobByURL, isBlobURL, waitForElement } from "../../dom";
import { blocksLibrary } from "..";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import CMSIcon from "./cms-icon";
import { GrigoraPreviewContext } from "../../post-preview/context";
import Toast from "../../toast/toast";
import { toast } from "react-toastify";

var IMG_SIZES = [1920, 1600, 1080, 800, 500, 300];

export const Image = (props) => {
  const {
    projectID,
    screen,
    uploadProjectImage,
    editorDocument = document,
    cmsPosts,
  } = useContext(GrigoraDashboardContext) ?? {};

  const previewContext = useContext(GrigoraPreviewContext);

  const {
    id,
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    dom,
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
    dom: state.dom,
  }));

  const {
    query: { node, parseFreshNode },
    actions: {
      add,
      delete: deleteCraftJSNode,
      selectNode,
      setProp: setPropGlobal,
    },
  } = useEditor();

  const parents = node(id).ancestors();

  const [srcModal, setSrcModal] = useState(false);

  var {
    isSrcDynamic,
    dynamicSrc,
    imageLink,
    src,
    srcMode,
    srcActionTrigger,
    caption,
    width,
    height,
    lazyLoad = true,
    altText,
    titleText,
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-image": true,
      [`image-${id}`]: id,
    },
    blockProps.className,
  );

  useEffect(() => {
    // Upload Action Trigger
    if (srcActionTrigger === "upload") {
      if (!isBlobURL(src)) {
        return;
      }

      const imageFile = getBlobByURL(src);

      uploadProjectImage(imageFile)
        .then((newImage) => {
          setProp((props) => {
            props.src = {
              ...newImage,
              projectID: newImage.projectID ? newImage.projectID : projectID,
            };
            props.srcMode = "cdn";
          });
        })
        .catch((err) => {
          setProp((props) => {
            props.src = undefined;
            props.srcMode = undefined;
          });
        });
    }
  }, [srcActionTrigger]);

  const imgsrc = useMemo(() => {
    var hasDynamicSrc = false;

    if (isSrcDynamic && dynamicSrc === "post-featured-image") {
      if (
        previewContext?.postData?.featured_image ||
        cmsPosts?.[0]?.featured_image
      ) {
        hasDynamicSrc = true;
        src = previewContext?.postData?.featured_image?.src
          ? previewContext?.postData?.featured_image?.src
          : cmsPosts?.[0]?.featured_image?.src;
        srcMode = previewContext?.postData?.featured_image?.srcMode
          ? previewContext?.postData?.featured_image?.srcMode
          : cmsPosts?.[0]?.featured_image?.srcMode;
      }
    }

    if (isSrcDynamic && !hasDynamicSrc) {
      return "https://cdn.grigora.co/defaults/img/post-featured-image-placeholder.webp";
    }

    if (srcMode === "cdn") {
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${
          src?.projectID ? src.projectID : projectID
        }/${src?.og}`;
      }
      return `https://cdn.grigora.co/projects/${
        src?.projectID ? src.projectID : projectID
      }/${src?.name}.${src?.extension}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src, isSrcDynamic, dynamicSrc]);

  const srcSet = useMemo(() => {
    if (isSrcDynamic) {
      return undefined;
    }

    if (src && src.width) {
      let available_sizes = IMG_SIZES.filter((value) => value <= src.width);
      let available_size_urls = [];
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${
          src?.projectID ? src.projectID : projectID
        }/${src?.og} ${src.width}w`;
      } else {
        available_size_urls.push(
          `https://cdn.grigora.co/projects/${
            src?.projectID ? src.projectID : projectID
          }/${src?.name}.${src?.extension} ${src.width}w`,
        );
      }
      available_sizes.map((size) => {
        if (src["resize" + size]) {
          available_size_urls.push(
            `https://cdn.grigora.co/projects/${
              src?.projectID ? src.projectID : projectID
            }/${src["resize" + size]} ${size}w`,
          );
        }
      });
      return available_size_urls.join(", ");
    }
    return undefined;
  }, [srcMode, src, isSrcDynamic, dynamicSrc]);

  const sizes = useMemo(() => {
    if (isSrcDynamic) {
      return undefined;
    }

    if (src && src.width) {
      return `(max-width: ${src.width}px) 100vw,  ${src.width}px`;
    }
    return undefined;
  }, [srcMode, src, isSrcDynamic, dynamicSrc]);

  return (
    <figure
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__Image(id, props)}`}</style>
      <ImagePickerUIKit
        src={src}
        srcMode={srcMode}
        onChange={(src, srcMode) => {
          setProp((props) => {
            props.src = src;
            props.srcMode = srcMode;
          });
        }}
        opened={srcModal}
        setOpened={setSrcModal}
      />

      {srcMode === undefined && !isSrcDynamic ? (
        <div className="text-gray-500 text-center">
          <div
            onClick={() => {
              setSrcModal(true);
            }}
            contentEditable={false}
            className="add-image-button"
          >
            Add an Image
          </div>
        </div>
      ) : (
        <>
          {srcMode === "preview" ? (
            <>
              <div className="absolute top-[45%] left-[45%] z-50">
                <LoaderSmall />
              </div>
              <img
                className="filter brightness-50"
                src={imgsrc}
                alt={`preview image`}
              />
            </>
          ) : (
            <>
              {imageLink ? (
                <a
                  href={imageLink}
                  onClick={(e) => {
                    e.preventDefault();
                    switch (e.detail) {
                      case 1:
                        break;
                      case 2:
                        if (isSrcDynamic) {
                          toast.info(
                            <Toast
                              title={"Image is non editable"}
                              description={
                                "Please check the dynamic source from block settings"
                              }
                            />,
                            {
                              // icon: <CorrectIcon />,
                            },
                          );
                          return;
                        }
                        setSrcModal(true);
                        break;
                      case 3:
                        break;
                    }
                  }}
                >
                  <Resizable
                    className="resizable"
                    nodeWidth={width}
                    nodeHeight={height}
                    onChange={(newWidth, newHeight) => {
                      setProp((props) => {
                        props.width = newWidth;
                        props.height = newHeight;
                      }, 300);
                    }}
                    useDynamicDimensions={true}
                    lockAspectRatio={true}
                    defaultSize={{
                      width: "fit-content",
                      height: "fit-content",
                    }}
                    active={selected}
                    onClick={(e) => {
                      switch (e.detail) {
                        case 1:
                          break;
                        case 2:
                          if (isSrcDynamic) {
                            toast.info(
                              <Toast
                                title={"Image is non editable"}
                                description={
                                  "Please check the dynamic source from block settings"
                                }
                              />,
                              {
                                // icon: <CorrectIcon />,
                              },
                            );
                            return;
                          }
                          setSrcModal(true);
                          break;
                        case 3:
                          break;
                      }
                    }}
                  >
                    <img
                      loading={lazyLoad ? "lazy" : "eager"}
                      decoding={"async"}
                      src={imgsrc}
                      srcSet={srcSet}
                      sizes={sizes}
                      title={titleText}
                      width={srcMode === "cdn" ? src.width : undefined}
                      height={srcMode === "cdn" ? src.height : undefined}
                      alt={`${
                        altText ? altText : "No alt attribute set on the image."
                      }`}
                    />
                  </Resizable>
                </a>
              ) : (
                <Resizable
                  className="resizable"
                  nodeWidth={width}
                  nodeHeight={height}
                  onChange={(newWidth, newHeight) => {
                    setProp((props) => {
                      props.width = newWidth;
                      props.height = newHeight;
                    }, 300);
                  }}
                  useDynamicDimensions={true}
                  lockAspectRatio={true}
                  defaultSize={{
                    width: "fit-content",
                    height: "fit-content",
                  }}
                  active={selected}
                  onClick={(e) => {
                    switch (e.detail) {
                      case 1:
                        break;
                      case 2:
                        if (isSrcDynamic) {
                          toast.info(
                            <Toast
                              title={"Image is non editable"}
                              description={
                                "Please check the dynamic source from block settings"
                              }
                            />,
                            {
                              // icon: <CorrectIcon />,
                            },
                          );
                          return;
                        }
                        setSrcModal(true);
                        break;
                      case 3:
                        break;
                    }
                  }}
                >
                  <img
                    loading={lazyLoad ? "lazy" : "eager"}
                    decoding={"async"}
                    src={imgsrc}
                    srcSet={srcSet}
                    sizes={sizes}
                    title={titleText}
                    width={srcMode === "cdn" ? src?.width : undefined}
                    height={srcMode === "cdn" ? src?.height : undefined}
                    alt={`${
                      altText ? altText : "No alt attribute set on the image."
                    }`}
                  />
                </Resizable>
              )}
            </>
          )}
          {(screen === "cms-post-editor" ? caption || selected : caption) && (
            <figcaption>
              <RichTextInput
                value={caption}
                onChange={(e, historyMergeAction) => {
                  if (historyMergeAction) {
                    historyMergeAction.setProp(
                      id,
                      (props) => {
                        props.caption = e;
                        return props;
                      },
                      500,
                    );
                    return;
                  }
                  setProp((props) => {
                    props.caption = e;
                    return props;
                  }, 500);
                }}
                tagName={"span"}
                placeholder={"Caption..."}
                onSplit={(newValue, oldValue, historyMergeAction) => {
                  const nodeID = getRandomNodeId();
                  const freshNode = {
                    id: nodeID,
                    data: {
                      type: blocksLibrary["grigora/text"],
                      props: {
                        ...(newValue.trim() ? props : {}),
                        content: newValue.trim(),
                      },
                    },
                  };
                  const nodeToAdd = parseFreshNode(freshNode).toNode();
                  var node_parent = undefined;
                  var node_index = undefined;
                  if (parents && parents.length > 0) {
                    node_parent = parents[0];
                  }
                  if (node_parent) {
                    let parentChildrens = node(node_parent).childNodes();
                    node_index = parentChildrens.indexOf(id) + 1;
                  }
                  if (historyMergeAction) {
                    historyMergeAction.add(nodeToAdd, node_parent, node_index);
                  } else {
                    add(nodeToAdd, node_parent, node_index);
                  }

                  selectNode(nodeID);
                  waitForElement(
                    editorDocument,
                    ".text-" + nodeID + " .grigora-richtext-input__field",
                  ).then((elm) => {
                    elm.focus();
                  });
                }}
              />
            </figcaption>
          )}
        </>
      )}
    </figure>
  );
};

// To Avoid Mangling in Production
Image.displayName = "Image";

Image.craft = {
  name: "Image",
  props: {},
  related: {
    settings: ImageSettings,
    CMSsettings: CMSImageSettings,
    icon: icon,
    CMSIcon: CMSIcon,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    category: "basic",
    keywords: ["Image", "Picture", "Photo", "Photograph"],
  },
  custom: {
    name: "Image",
    slug: "grigora/image",
  },
};
